/**
 * Tableau « Analyse SEO des URLs » — structure enrichie (UI + export JSON/IA).
 */

import decodeHtml from './decodeHtml';
import { isReferencePage } from './referencePages';
import {
  evaluateSeoCriteria,
  seoImpactPhrase,
  getTitleLengthStatus,
  getMetaLengthStatus,
  SEO_THRESHOLDS,
} from './seoCriteria';
import { getScoreQualitative } from './scoreLabels';

const CRITERIA_KEYS = [
  { key: 'titleOk', label: 'Title', hint: '50–70 car.' },
  { key: 'metaOk', label: 'Meta desc.', hint: '120–170 car.' },
  { key: 'h1Ok', label: 'H1', hint: '≥ 1 balise H1' },
  { key: 'imagesOk', label: 'Images alt', hint: 'Tous les alt présents' },
  { key: 'canonOk', label: 'Canonical', hint: 'Balise présente' },
];

function formatSeoUrlCriteriaDetail(c, titleStatus, metaStatus, audit) {
  const h1 = decodeHtml(audit.h1 || '');
  const parts = [
    `Title (${c.titleLen} car.) : ${c.titleOk ? 'OK' : 'À corriger'} — ${titleStatus.label}${titleStatus.recommendation ? ` — ${titleStatus.recommendation}` : ''}`,
    `Meta (${c.metaLen} car.) : ${c.metaOk ? 'OK' : 'À corriger'} — ${metaStatus.label}${metaStatus.recommendation ? ` — ${metaStatus.recommendation}` : ''}`,
    `H1 (${c.h1Count}) : ${c.h1Ok ? 'OK' : 'Manquant ou incorrect'}${h1 ? ` — « ${h1} »` : ''}`,
    `Images : ${c.nbImages} total, ${c.nbWithoutAlt} sans alt — ${c.imagesOk ? 'OK' : 'À corriger'}`,
    `Canonical : ${c.canonOk ? 'OK' : 'À vérifier'}${audit.canonical_url ? ` — ${audit.canonical_url}` : ''}`,
  ];
  return parts.join('\n');
}

function buildSeoUrlRowNarrative(row) {
  const blocks = [
    `URL : ${row.url}`,
    row.isReferencePage ? 'Page de référence : oui' : '',
    `Score SEO : ${row.scoreSeo ?? '—'}${row.scoreSeoLabel ? ` (${row.scoreSeoLabel})` : ''}`,
    `Titre : ${row.title || '—'}`,
    `Meta : ${row.metaDescription || '—'}`,
    `H1 : ${row.h1 || '—'}`,
    row.criteriaDetail,
    `Impact business : ${row.businessImpact}`,
  ];
  return blocks.filter(Boolean).join('\n');
}

function buildCriteriaHealthSummary(rows) {
  const valid = rows.filter((r) => r.scoreSeo > 0);
  const n = valid.length;
  if (!n) return { pagesAnalyzed: 0, criteria: [] };

  return {
    pagesAnalyzed: n,
    criteria: CRITERIA_KEYS.map(({ key, label, hint }) => {
      const count = valid.filter((r) => r.criteria[key]).length;
      const percent = Math.round((count / n) * 100);
      return { key, label, hint, okCount: count, total: n, percentOk: percent };
    }),
  };
}

export function buildSeoUrlExportRow(project, audit) {
  const c = evaluateSeoCriteria(audit);
  const title = decodeHtml(audit.title || '');
  const meta = decodeHtml(audit.meta_description || '');
  const h1 = decodeHtml(audit.h1 || '');
  const titleStatus = getTitleLengthStatus(c.titleLen, !!title);
  const metaStatus = getMetaLengthStatus(c.metaLen, !!meta);
  const score = Math.round(Number(audit.score_global) || 0);
  const q = score > 0 ? getScoreQualitative(score) : null;
  const criteriaDetail = formatSeoUrlCriteriaDetail(c, titleStatus, metaStatus, audit);

  const row = {
    url: audit.url,
    isReferencePage: isReferencePage(audit.url, project),
    scoreSeo: score > 0 ? score : null,
    scoreSeoLabel: q?.label ?? null,
    title,
    titleLen: c.titleLen,
    titleOk: c.titleOk,
    titleStatus: {
      label: titleStatus.label,
      tone: titleStatus.tone,
      recommendation: titleStatus.recommendation,
    },
    metaDescription: meta,
    metaLen: c.metaLen,
    metaOk: c.metaOk,
    metaStatus: {
      label: metaStatus.label,
      tone: metaStatus.tone,
      recommendation: metaStatus.recommendation,
    },
    h1,
    h1Count: c.h1Count,
    h1Ok: c.h1Ok,
    nbImages: c.nbImages,
    nbWithoutAlt: c.nbWithoutAlt,
    imagesOk: c.imagesOk,
    canonicalUrl: audit.canonical_url || '',
    canonicalOk: c.canonOk,
    businessImpact: seoImpactPhrase(c),
    criteria: {
      titleOk: c.titleOk,
      metaOk: c.metaOk,
      h1Ok: c.h1Ok,
      imagesOk: c.imagesOk,
      canonOk: c.canonOk,
    },
    criteriaDetail,
    rowNarrativeForAi: '',
  };
  row.rowNarrativeForAi = buildSeoUrlRowNarrative(row);
  return row;
}

export const SEO_URLS_TABLE_COLUMNS = [
  'url',
  'isReferencePage',
  'scoreSeo',
  'scoreSeoLabel',
  'title',
  'titleLen',
  'titleOk',
  'titleStatus',
  'metaDescription',
  'metaLen',
  'metaOk',
  'metaStatus',
  'h1',
  'h1Count',
  'h1Ok',
  'nbImages',
  'nbWithoutAlt',
  'imagesOk',
  'canonicalUrl',
  'canonicalOk',
  'businessImpact',
  'criteriaDetail',
  'rowNarrativeForAi',
];

/**
 * Tableau complet aligné sur l’onglet SEO + format exploitable IA.
 */
export function buildSeoUrlsExportTable(project, audits) {
  const rows = (audits || [])
    .filter((r) => r && r.url && !r.loading && !r.error)
    .map((r) => buildSeoUrlExportRow(project, r));

  const summary = buildCriteriaHealthSummary(rows);
  const fullTableNarrativeForAi = rows
    .map((r) => r.rowNarrativeForAi)
    .join('\n\n---\n\n');

  return {
    title: 'Analyse SEO des URLs',
    thresholds: SEO_THRESHOLDS,
    totalRows: rows.length,
    referencePagesCount: rows.filter((r) => r.isReferencePage).length,
    summary,
    columns: SEO_URLS_TABLE_COLUMNS,
    rows,
    fullTableNarrativeForAi,
  };
}
